import React, { Component } from "react";
import { observer } from "mobx-react";
import PricingStore from "../../../stores/PricingStore";
import {
  Pricing as PricingQuote,
  PricingState,
} from "../../../stores/PricingStore/Types";
import PricingGrid from "../../Home/components/NewPricing/PricingGrid";
import Cast from "../../Home/components/Cast";
import HeroTrustStrip from "../../CoursePreview/components/HeroTrustStrip";
import { learnerCountDisplay } from "../../../utils/learnerCount";
import Proof from "./Proof";
import { monthsFree } from "../../../utils/helpers";

// The 42courses logo blob, as the About page's header uses it: the body and
// its dot, mirrored for variety without drifting off the logo shape.
const BLOB_BODY =
  "M192.43,117.14c11.53-46.12-30-39.2-53-53s-17.5-33.22-57.65-36.89S14.78,47.91,8,82.55s.25,82.91,53,94.55S180.9,163.26,192.43,117.14Z";
const BLOB_DOT =
  "M149.36,21.44c-6.46,4.67-18.7,21.13-8.53,30.51s27.73,6.23,35.8-8.82c7.22-13.44-2.11-20.87-8.75-23.83S153.07,18.82,149.36,21.44Z";

interface Props {
  pricingStore: PricingStore;
  handleShowSignInSideBar: (redirectUrl?: string) => void;
}


// The questions people actually ask before paying. Written to be read in one
// go and, where it costs nothing, to raise a smile. "All Courses Pass" is
// always said in full: on its own, "pass" describes nothing.
//
// The same answers, minus the links, live in the page's structured data in
// app/views/application/seo_tags/_pricing.html.erb. Change both.
const faqs = [
  {
    question: "Is the All Courses Pass worth it?",
    answer:
      "If you'll do more than two courses in a year, yes, comfortably. The All Courses Pass costs about the same as two single courses and gives you all forty-odd, every certificate, and the Community Hub for the whole year rather than three months. If you only want one course, buy one course. We'd rather you were happy than upsold.",
  },
  {
    question: "What do I get with a single course?",
    answer:
      "The whole course, yours for life, with every answer marked by a real person, a CPD-accredited Mini MBA certificate at the end, and three months of the Community Hub thrown in.",
  },
  {
    question: "Is 42courses CPD accredited?",
    answer:
      "Yes. Every course is CPD accredited, and every certificate is signed, dated and trackable. Hang it on the wall, add it to LinkedIn, or leave it somewhere your peers will see it. All three are popular.",
  },
  {
    question: "Can my employer pay?",
    answer:
      'Yes. Plenty of learners put a course through as training. At checkout, click "I need a company invoice" and add your company details. They\'ll go on your invoice, ready to forward to whoever holds the budget. If your company would rather pay by bank transfer, or needs an invoice before anyone clicks anything, email <a href="mailto:groundcontrol@42courses.com">groundcontrol@42courses.com</a> and we\'ll sort it by hand. If you need a quote or a brochure to make the case first, the <a href="/team_plans">Teams page</a> has both.',
  },
  {
    question: "What currency will I be charged in?",
    answer:
      "The one you can see. Prices are set for the country you're in and shown in your local currency. If you'd rather see them in a different currency, use the switcher in the menu and the numbers change to match. Whatever is shown at checkout is what your card is charged, and nothing else.",
  },
  {
    question: "Do prices go up after the first year?",
    answer:
      "No. The price you pay for the All Courses Pass is locked in for as long as you stay subscribed, even if the public price goes up later. We love rewarding loyalty.",
  },
  {
    question: "Can I cancel or get a refund?",
    answer:
      "The Community Hub is monthly and you can cancel whenever you like. Everything comes with a 30-day refund window: if you aren't happy, we aren't happy, and we'd rather put that right than keep the money.",
  },
  {
    question: "Can I give a course as a gift?",
    answer:
      'Yes, and people do, especially in December. At checkout choose "Buying this for someone else?", or start from the <a href="/gift">gift page</a>, and we\'ll take it from there.',
  },
  {
    question: "Is there pricing for teams?",
    answer:
      'Yes. Teams of 10 or more get their own plan with progress tracking and a team leaderboard, which gets more competitive than anyone admits. The <a href="/team_plans">42courses Teams page</a> has all the answers.',
  },
];

class Pricing extends Component<Props, {}> {
  componentDidMount() {
    this.props.pricingStore.setQuotationParams({ page: "pricing" });
  }

  componentWillUnmount() {
    this.props.pricingStore.setQuotationParams({});
  }

  wholeAmount = (amount?: string) => amount?.replace(/[.,]\d{1,2}$/, "");

  firstFaqAnswer = (pricingState: PricingState) => {
    if (
      pricingState.kind !== "ready" ||
      pricingState.pricing.kind !== "pricing"
    ) {
      return "42courses offers three ways in: a single course, the All Courses Pass for a year, and Lifetime All Access.";
    }

    const pricing: PricingQuote = pricingState.pricing;
    const yearly = pricing.yearly_pricing;
    const single = pricing.single_course_pricing;
    const lifetime = pricing.lifetime_pricing;
    const money = (symbol: string, amount?: string) =>
      `${symbol}${this.wholeAmount(amount)}`;

    // Full prices first, as the cards above say them, then the monthly way
    // of paying with what it adds up to.
    const singlePaymentPlan =
      single.payment_plan_amount && single.payment_plan_installment_count
        ? `, or ${single.payment_plan_installment_count} monthly payments of ${money(
            single.currency_symbol,
            single.payment_plan_amount,
          )} at 0% interest`
        : "";
    const yearlyFree = yearly.upfront_months_free
      ? ` (${monthsFree(yearly.upfront_months_free)})`
      : "";
    const lifetimeLine = lifetime?.amount
      ? ` Lifetime All Access is ${money(lifetime.currency_symbol, lifetime.amount)} ${
          lifetime.currency_code
        }, paid once, for every course we have and every one we make.`
      : "";

    return `42courses offers three ways in. A single course is ${money(
      single.currency_symbol,
      single.amount,
    )} ${single.currency_code}, paid once${singlePaymentPlan}. The All Courses Pass is ${money(
      yearly.currency_symbol,
      yearly.amount,
    )} ${yearly.currency_code} for a year, paid upfront${yearlyFree}, or 12 monthly payments of ${money(
      yearly.currency_symbol,
      yearly.calculated_monthly_amount,
    )} (${money(yearly.currency_symbol, yearly.monthly_total)} in total, 0% interest).${lifetimeLine}`;
  };

  pricingFaqs = (pricingState: PricingState) => [
    {
      question: "How much does 42courses cost?",
      answer: this.firstFaqAnswer(pricingState),
    },
    ...faqs,
  ];

  renderPricing = (state: PricingState) => {
    switch (state.kind) {
      case "ready":
        if (state.pricing.kind !== "pricing") {
          return <div className="no-pricing" />;
        }

        return (
          <>
            <PricingGrid pricing={state.pricing} page="home" showHeader={false} showFooter={false} />
            <p className="anchor-note">
              For context: a typical Mini MBA costs{" "}
              <strong>about £2,000</strong>, and covers one subject. The All
              Courses Pass covers forty-odd.
            </p>
            <div className="pricing-footer">
              Running training for a team? See our{" "}
              <a href="/team_plans">Teams plan</a> and{" "}
              <a href="/workshops">live workshops →</a>
              <p className="gift-note">
                <svg aria-hidden="true">
                  <use href="#pricing-icon-gift"></use>
                </svg>
                All plans available as gifts at checkout.{" "}
                <a href="/gift">Gift a course or an All Courses Pass →</a>
              </p>
            </div>
          </>
        );
      case "loading":
        return (
          <section className="pricing pricing--default center aligned">
            <div className="container" id="pricing-container">
              <div className="ui active inverted dimmer">
                <div className="ui text loader">Loading</div>
              </div>
            </div>
          </section>
        );
      default:
        return <div className="no-pricing" />;
    }
  };

  render() {
    const pricingState = this.props.pricingStore.pricingState;
    const pricingFaqs = this.pricingFaqs(pricingState);

    return (
      <>
        <svg
          width="0"
          height="0"
          style={{ position: "absolute" }}
          aria-hidden="true"
          focusable="false"
        >
          <symbol id="pricing-icon-gift" viewBox="0 0 36 36">
            <rect
              x="5"
              y="18"
              width="26"
              height="14"
              rx="2"
              fill="#F5B041"
              fillOpacity=".12"
              stroke="#C4841D"
              strokeWidth="3"
              strokeLinejoin="round"
            />
            <rect
              x="3"
              y="12"
              width="30"
              height="7"
              rx="2"
              fill="#F5B041"
              fillOpacity=".12"
              stroke="#C4841D"
              strokeWidth="3"
              strokeLinejoin="round"
            />
            <line
              x1="18"
              y1="12"
              x2="18"
              y2="32"
              stroke="#C4841D"
              strokeWidth="2.5"
            />
            <ellipse
              cx="14"
              cy="9"
              rx="4.5"
              ry="3.5"
              fill="none"
              stroke="#C4841D"
              strokeWidth="3"
            />
            <ellipse
              cx="22"
              cy="9"
              rx="4.5"
              ry="3.5"
              fill="none"
              stroke="#C4841D"
              strokeWidth="3"
            />
            <circle cx="18" cy="10" r="2.5" fill="#C4841D" />
          </symbol>
        </svg>
        <main className="pages" id="pricing">
          <section className="pricing-hero" aria-labelledby="pricing-h1">
            <div className="hero-pattern" aria-hidden="true">
              <svg
                width="100%"
                height="100%"
                preserveAspectRatio="xMidYMid slice"
                xmlns="http://www.w3.org/2000/svg"
              >
                <defs>
                  <g id="pricing-blob-1" transform="translate(-100,-100)">
                    <path d={BLOB_BODY} />
                    <path d={BLOB_DOT} />
                  </g>
                  <g id="pricing-blob-2" transform="translate(100,-100) scale(-1,1)">
                    <path d={BLOB_BODY} />
                    <path d={BLOB_DOT} />
                  </g>
                </defs>
                <svg x="8%" y="20%" overflow="visible">
                  <use xlinkHref="#pricing-blob-1" fill="#FFA5BC" transform="scale(2.4)" opacity="0.16" />
                </svg>
                <svg x="82%" y="78%" overflow="visible">
                  <use xlinkHref="#pricing-blob-2" fill="#B798F2" transform="scale(3.0)" opacity="0.15" />
                </svg>
                <svg x="48%" y="-12%" overflow="visible">
                  <use xlinkHref="#pricing-blob-1" fill="#E8EBFF" transform="scale(2.0)" opacity="0.5" />
                </svg>
                <svg x="62%" y="62%" overflow="visible">
                  <use xlinkHref="#pricing-blob-1" fill="#F3EC7A" transform="scale(1.4)" opacity="0.32" />
                </svg>
              </svg>
            </div>
            <div className="wrap">
              <span className="eyebrow">Pricing</span>
              {/* The same line as the pricing on the home and course pages. */}
              <h1 className="hero-title" id="pricing-h1">
                Join{" "}
                <span className="nobr">
                  <em>{learnerCountDisplay()} happy learners</em>.
                </span>
              </h1>
              <p className="hero-sub">
                Three plans, one 30-day refund promise, and your price never
                goes up while you stay subscribed.
              </p>
            </div>
          </section>
          {/* Proof before the price, as on a course page: the logos of where
              learners work, then the people they learn from under the plans. */}
          <HeroTrustStrip />
          {this.renderPricing(pricingState)}
          <Cast />
          <Proof />
          {/* Built the way the Teams page builds its FAQ, so the two read as
              one site: cream band, eyebrow and title, white cards with a plus
              that turns into a cross. */}
          <section
            className="pricing-faq-section"
            aria-labelledby="pricing-faq-title"
          >
            <div className="pricing-page__container">
              <div className="pricing-section-header">
                <span className="pricing-section-eyebrow">Frequently asked</span>
                <h2 className="pricing-section-title" id="pricing-faq-title">
                  The questions people ask before they pay.
                </h2>
              </div>
              <div className="pricing-faq-list">
                {pricingFaqs.map((faq) => (
                  <details className="pricing-faq-item" key={faq.question}>
                    <summary>{faq.question}</summary>
                    <div
                      className="pricing-answer"
                      dangerouslySetInnerHTML={{ __html: faq.answer }}
                    ></div>
                  </details>
                ))}
              </div>
              <p className="more">
                More questions? The <a href="/faqs">full FAQs</a> have the lot.
              </p>
            </div>
          </section>
        </main>
      </>
    );
  }
}

export default observer(Pricing);
